'use client';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { defaultDataTagGroup } from '@/constants/defaultSeo';
import { customRemarkComponents } from '@/tools/layout';
import { IComponentFrequentlyAskedQuestions } from '@/types/cms';
import { RichText } from '@lamesarv-sdk/components';
import { PropsWithClasses } from '@lamesarv-sdk/types';

import { CommonImage } from '../CommonImage';

interface IFaqItemProps extends PropsWithClasses {
  title: string;
  text: string;
  isOpen?: boolean;
  iconUrl?: string;
  classesContainer?: string;
  classesTitle?: string;
  classesText?: string;
  classesIcon?: string;
}

const FAQItem = ({
  classesContainer,
  classesText,
  classesTitle,
  text,
  title,
  isOpen = false,
  classesIcon,
  iconUrl,
}: IFaqItemProps) => {
  const [isItemOpen, setIsItemOpen] = useState<boolean>(!!isOpen);

  return (
    <div className={twMerge(styles.question, classesContainer)}>
      <div className={styles.questionHeader} onClick={() => setIsItemOpen(!isItemOpen)}>
        <div className="flex items-center gap-4">
          {iconUrl && <CommonImage src={iconUrl} className={twMerge('w-7', classesIcon)} />}
          <div className={twMerge(styles.questionTitle, classesTitle)}>{title}</div>
        </div>

        <div className={styles.questionIcon}>{isItemOpen ? '-' : '+'}</div>
      </div>
      <div className={isItemOpen ? 'block' : 'hidden'}>
        <RichText
          content={text}
          customRemarkComponents={customRemarkComponents}
          className={twMerge(styles.questionText, classesText)}
        />
      </div>
    </div>
  );
};

export const FrequentlyAskedQuestions = ({
  classes,
  classes_container,
  classes_text,
  classes_section,
  classes_title,
  data_tag,
  data_tag_group,
  questions,
  text,
  title,
}: IComponentFrequentlyAskedQuestions) => {
  return (
    <div
      className={twMerge(styles.section, classes_section)}
      data-tag={data_tag || 'frequently-asked-questions'}
      data-tag-group={data_tag_group || defaultDataTagGroup}
    >
      <div className={twMerge(styles.container, classes_container)}>
        <div className={twMerge(styles.component, classes)}>
          <div className={styles.header}>
            <div className={twMerge(styles.title, classes_title)}>{title}</div>
            <div className={twMerge(styles.text, classes_text)}>
              <RichText content={text} customRemarkComponents={customRemarkComponents} />
            </div>
          </div>
          <div className={styles.content}>
            {questions.map((question, index) => (
              <FAQItem
                key={index}
                title={question.title}
                text={question.text}
                isOpen={question.is_open}
                iconUrl={question.icon_url}
                classesContainer={question.classes_container}
                classesText={question.classes_text}
                classesTitle={question.classes_title}
                classesIcon={question.classes_icon}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  section: 'bg-white font-helvetica text-blue-700',
  container: 'py-[30px] md:py-[50px] px-8 md:px-32 xl:px-4 xl:container mx-auto',
  component: 'flex flex-col xl:flex-row gap-6 xl:gap-12',
  header: 'flex-none flex flex-col gap-4 xl:w-[480px]',
  content: 'grow flex flex-col',
  title: 'text-blue-700 text-[24px] md:text-[34px] font-bold',
  text: 'text-blue-700 text-[14px] md:text-[16px]',
  question: 'flex flex-col w-full border-b border-black-100 py-6',
  questionHeader: 'flex flex-row gap-2 items-center justify-between w-full cursor-pointer leading-none',
  questionTitle: 'text-blue-700 text-[14px] md:text-[16px] font-bold',
  questionIcon: 'text-blue-700 text-[16px] font-bold',
  questionText: 'text-[14px] md:text-[16px] pt-4',
};
